import CloseIcon from "@redotech/redo-icons/x-close.svg";
import classNames from "classnames";
import { memo, ReactNode } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoButton } from "../buttons/redo-button";
import {
  RedoFeaturedIcon,
  RedoFeaturedIconProps,
} from "../featured-icon/redo-featured-icon";
import * as redoNotificationCss from "./redo-notification.module.css";

export interface NotificationButton {
  text: string;
  onClick(): void | Promise<void>;
}

export const notificationBreakpoint = ["mobile", "desktop"] as const;
export type NotificationBreakpoint = (typeof notificationBreakpoint)[number];

export interface NotificationProps {
  title?: string;
  description?: string;
  hideCloseX?: boolean;
  dismiss: () => void;
  primaryButton?: NotificationButton;
  dismissButton?: Omit<NotificationButton, "onClick">;
  featuredIcon?: Omit<RedoFeaturedIconProps, "size">;
  breakpoint?: NotificationBreakpoint;
  children?: (dismiss: () => void) => ReactNode;
  fullModalImage?: string;
  dataTestId?: string;
  noPadding?: boolean;
}

/**
 * To actually show a notification, use the function {@link showNotification}
 */
export const RedoNotification = memo(function RedoNotification({
  title,
  description,
  primaryButton,
  dismissButton,
  hideCloseX,
  dismiss,
  breakpoint = "desktop",
  featuredIcon,
  children = () => null,
  fullModalImage,
  dataTestId,
  noPadding = false,
}: NotificationProps) {
  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      className={redoNotificationCss.container}
      data-testid={dataTestId}
      dir={breakpoint === "desktop" ? "row" : "column"}
      gap={noPadding ? "none" : "xl"}
      p={noPadding ? "none" : "xl"}
      radius="md"
    >
      {fullModalImage && (
        <Flex className={redoNotificationCss.fullModalImage} radius="md">
          <img alt="Full modal image" src={fullModalImage} />
        </Flex>
      )}
      <Flex
        className={classNames(
          redoNotificationCss.contentContainer,
          breakpointToClass[breakpoint],
          fullModalImage ? redoNotificationCss.withImage : "",
        )}
      >
        {featuredIcon && (
          <Flex>
            <RedoFeaturedIcon {...featuredIcon} size="md" />
          </Flex>
        )}
        <Flex
          dir="column"
          gap="lg"
          grow={1}
          pr={
            breakpoint === "desktop" &&
            (dismissButton || primaryButton || !hideCloseX)
              ? "4xl"
              : "none"
          }
        >
          {title || description ? (
            <Flex dir="column" gap="xs">
              {title && (
                <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                  {title}
                </Text>
              )}
              {description && (
                <Text fontSize="sm" fontWeight="regular" textColor="secondary">
                  {description}
                </Text>
              )}
            </Flex>
          ) : null}
          {children(dismiss)}
          {dismissButton || primaryButton ? (
            <Flex>
              {dismissButton && (
                <RedoButton
                  hierarchy="secondary"
                  onClick={dismiss}
                  size="sm"
                  text={dismissButton.text}
                />
              )}
              {primaryButton && (
                <RedoButton
                  hierarchy="primary"
                  onClick={async () => {
                    await primaryButton.onClick();
                    dismiss();
                  }}
                  size="sm"
                  text={primaryButton.text}
                />
              )}
            </Flex>
          ) : null}
        </Flex>
        {!hideCloseX && (
          <RedoButton
            aria-label="Dismiss"
            className={redoNotificationCss.closeButton}
            hierarchy="tertiary"
            IconLeading={CloseIcon}
            onClick={dismiss}
            size="md"
          />
        )}
      </Flex>
    </Flex>
  );
});

const breakpointToClass: Record<NotificationBreakpoint, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  mobile: redoNotificationCss.mobile,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  desktop: redoNotificationCss.desktop,
};
